import { useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react'
import { Box, Typography, useTheme } from '@mui/material'
import { palette } from '../theme/tokens.js'
import { couleursCategories } from './palette.js'

/**
 * Graphe en colonnes du tableau de bord — SVG écrit à la main.
 *
 * ⭐ Sans bibliothèque, délibérément : l'application est une PWA servie sur des
 * réseaux lents, et une bibliothèque de graphes pèse à elle seule autant qu'un
 * écran entier. Ce composant ne sait faire que ce dont le tableau de bord a
 * besoin — des colonnes, empilées ou non, et une ligne sur le MÊME axe.
 *
 * ⚠️ Jamais de second axe vertical. Deux mesures d'échelles différentes sur un
 * même graphe se lisent l'une contre l'autre alors qu'elles ne se comparent
 * pas ; ici la ligne est toujours dans l'unité des colonnes (le bénéfice net
 * face au chiffre d'affaires, en francs tous les deux).
 */

/**
 * Palette catégorielle du graphe, par mode.
 *
 * ⚠️ VALIDÉE, pas choisie à l'œil : contraste ≥ 3:1 sur la surface, écart
 * perceptif ≥ 30 sous protanopie et deutéranopie, en clair (#FFFFFF) comme en
 * sombre (#0F172A). Le vert et le rouge du thème sont réservés aux STATUTS —
 * un bénéfice en vert se lirait comme « tout va bien ».
 */
export function couleursGraphe(sombre: boolean): readonly [string, string] {
  // Les deux premières teintes de LA palette commune — pas une copie.
  const [a, b] = couleursCategories(sombre)
  return [a ?? palette.brand[500], b ?? '#eb6834']
}

export interface SerieGraphe {
  nom: string
  /** Une valeur par tranche. `null` : pas de donnée — ni colonne, ni point. */
  valeurs: readonly (number | null)[]
}

export interface GrapheColonnesProps {
  /** Nom accessible du graphe. */
  titre: string
  /** Libellé court de chaque tranche, sous l'axe. */
  libelles: readonly string[]
  /** Libellé complet de chaque tranche, dans l'infobulle. */
  libellesLongs: readonly string[]
  /** Une série : colonnes simples. Plusieurs : colonnes EMPILÉES, dans l'ordre. */
  colonnes: readonly SerieGraphe[]
  /** Série tracée en ligne par-dessus les colonnes, sur le même axe. */
  ligne?: SerieGraphe
  /** Valeur complète — infobulle. */
  formater: (v: number) => string
  /** Valeur abrégée — graduations et étiquette de la dernière colonne. */
  formaterAxe: (v: number) => string
  /** Des effectifs : graduations entières, jamais « 2,5 ventes ». */
  entiers?: boolean
  /**
   * Masque la légende — seulement quand la carte en porte déjà une, chiffrée,
   * avec les mêmes pastilles. Deux légendes identiques l'une sous l'autre ne
   * disent rien de plus.
   */
  sansLegende?: boolean
}

const HAUTEUR = 240
const MARGE_HAUT = 22
const MARGE_BAS = 26
const MARGE_DROITE = 8
/** Épaisseur maximale d'une colonne : au-delà, elle devient un bloc. */
const COLONNE_MAX = 24
const ECART = 2

/** Graduations « rondes » couvrant [min, max]. */
export function graduations(min: number, max: number, entiers: boolean): number[] {
  const etendue = max - min || 1
  const brut = etendue / 4
  const puissance = 10 ** Math.floor(Math.log10(brut))
  const facteurs = entiers ? [1, 2, 5, 10] : [1, 2, 2.5, 5, 10]
  let pas = (facteurs.find((f) => f * puissance >= brut) ?? 10) * puissance
  if (entiers) pas = Math.max(1, Math.round(pas))
  const debut = Math.floor(min / pas) * pas
  const fin = Math.ceil(max / pas) * pas
  const liste: number[] = []
  for (let v = debut; v <= fin + pas / 2; v += pas) liste.push(Math.round(v * 1000) / 1000)
  return liste.length >= 2 ? liste : [debut, debut + pas]
}

/**
 * Tracé d'une colonne dont seule l'EXTRÉMITÉ est arrondie.
 *
 * `y0` est le côté de la ligne de base (carré), `y1` celui de la donnée
 * (arrondi à 4 px). Fonctionne vers le haut comme vers le bas.
 */
function chemin(x: number, w: number, y0: number, y1: number, arrondi: boolean): string {
  const h = Math.abs(y1 - y0)
  const s = y1 < y0 ? 1 : -1
  const r = arrondi ? Math.min(4, w / 2, h) : 0
  return (
    `M${x},${y0} V${y1 + s * r} Q${x},${y1} ${x + r},${y1} ` +
    `H${x + w - r} Q${x + w},${y1} ${x + w},${y1 + s * r} V${y0} Z`
  )
}

export function useLargeur(): [React.RefObject<HTMLDivElement>, number] {
  const ref = useRef<HTMLDivElement>(null)
  const [largeur, setLargeur] = useState(0)
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    setLargeur(Math.floor(el.getBoundingClientRect().width))
    const observateur = new ResizeObserver(([entree]) => {
      if (entree) setLargeur(Math.floor(entree.contentRect.width))
    })
    observateur.observe(el)
    return () => observateur.disconnect()
  }, [])
  return [ref, largeur]
}

export function GrapheColonnes({
  titre,
  libelles,
  libellesLongs,
  colonnes,
  ligne,
  formater,
  formaterAxe,
  entiers = false,
  sansLegende = false,
}: GrapheColonnesProps): JSX.Element {
  const theme = useTheme()
  const sombre = theme.palette.mode === 'dark'
  const couleurs = couleursGraphe(sombre)
  const surface = theme.palette.background.paper
  const [ref, largeur] = useLargeur()
  const [actif, setActif] = useState<number | null>(null)

  const n = libelles.length
  const series = [...colonnes, ...(ligne ? [ligne] : [])]
  const couleurDe = (index: number): string => couleurs[index % couleurs.length] ?? couleurs[0]

  const vide = series.every((s) => s.valeurs.every((v) => v === null || v === 0))

  // Étendue : les PILES de colonnes (positif et négatif séparément) et la ligne.
  let min = 0
  let max = 0
  for (let i = 0; i < n; i++) {
    let pos = 0
    let neg = 0
    for (const s of colonnes) {
      const v = s.valeurs[i] ?? 0
      if (v > 0) pos += v
      else neg += v
    }
    max = Math.max(max, pos)
    min = Math.min(min, neg)
    const l = ligne?.valeurs[i]
    if (l !== null && l !== undefined) {
      max = Math.max(max, l)
      min = Math.min(min, l)
    }
  }
  if (max === min) max = min + (entiers ? 1 : 1000)
  const ticks = graduations(min, max, entiers)
  const bas = ticks[0] ?? 0
  const haut = ticks.at(-1) ?? 1

  const margeGauche = Math.max(...ticks.map((t) => formaterAxe(t).length)) * 7 + 12
  const plotW = Math.max(0, largeur - margeGauche - MARGE_DROITE)
  const plotH = HAUTEUR - MARGE_HAUT - MARGE_BAS
  const y = (v: number): number => MARGE_HAUT + ((haut - v) / (haut - bas)) * plotH
  const slot = n > 0 ? plotW / n : 0
  const cx = (i: number): number => margeGauche + slot * (i + 0.5)
  const lb = Math.max(2, Math.min(COLONNE_MAX, slot * 0.7))

  // Étiquettes sous l'axe : SÉLECTIVES, comptées depuis la dernière pour que
  // la tranche en cours — celle de la carte — soit toujours nommée.
  const maxEtiquettes = Math.max(1, Math.floor(plotW / 52))
  const pasEtiquette = Math.max(1, Math.ceil(n / maxEtiquettes))

  function surTouche(e: KeyboardEvent): void {
    if (n === 0) return
    const courant = actif ?? n - 1
    const cible =
      e.key === 'ArrowLeft'
        ? Math.max(0, courant - 1)
        : e.key === 'ArrowRight'
          ? Math.min(n - 1, courant + 1)
          : e.key === 'Home'
            ? 0
            : e.key === 'End'
              ? n - 1
              : null
    if (e.key === 'Escape') setActif(null)
    if (cible === null) return
    e.preventDefault()
    setActif(cible)
  }

  // Le tracé de la ligne, interrompu là où la donnée manque.
  let traceLigne = ''
  let dernierPoint: { x: number; y: number } | null = null
  if (ligne) {
    let leve = true
    ligne.valeurs.forEach((v, i) => {
      if (v === null) {
        leve = true
        return
      }
      traceLigne += `${leve ? 'M' : 'L'}${cx(i)},${y(v)} `
      dernierPoint = { x: cx(i), y: y(v) }
      leve = false
    })
  }
  const pointFinal = dernierPoint as { x: number; y: number } | null

  // Étiquette de la dernière colonne — la valeur de la carte —, seulement
  // quand aucune ligne ne vient la disputer.
  const totalFinal = colonnes.reduce((s, c) => s + (c.valeurs[n - 1] ?? 0), 0)

  return (
    <Box>
      {series.length >= 2 && !vide && !sansLegende ? (
        // ⭐ Une légende dès deux séries : l'identité ne repose jamais sur la
        // seule couleur. La pastille reprend la marque — rectangle pour une
        // colonne, trait pour la ligne.
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 2 }}>
          {series.map((s, k) => (
            <Box key={s.nom} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {ligne && k === series.length - 1 ? (
                <Box sx={{ width: 14, height: 2, borderRadius: 1, bgcolor: couleurDe(k) }} />
              ) : (
                <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: couleurDe(k) }} />
              )}
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {s.nom}
              </Typography>
            </Box>
          ))}
        </Box>
      ) : null}

      <Box
        ref={ref}
        tabIndex={0}
        role="group"
        aria-label={`${titre}. Flèches gauche et droite pour parcourir les périodes.`}
        onKeyDown={surTouche}
        onFocus={() => setActif((a) => a ?? n - 1)}
        onBlur={() => setActif(null)}
        sx={{
          position: 'relative',
          height: HAUTEUR,
          outline: 'none',
          borderRadius: 1,
          '&:focus-visible': { boxShadow: `0 0 0 2px ${theme.palette.primary.main}` },
        }}
      >
        {vide ? (
          <Box
            sx={{
              height: '100%',
              display: 'grid',
              placeItems: 'center',
              color: 'text.secondary',
            }}
          >
            <Typography variant="body2">Aucune donnée sur la période.</Typography>
          </Box>
        ) : largeur > 0 ? (
          <svg
            width={largeur}
            height={HAUTEUR}
            role="img"
            aria-label={titre}
            onPointerLeave={() => setActif(null)}
            style={{ display: 'block', overflow: 'visible' }}
          >
            {/* Grille : filets de 1 px, pleins, en retrait. */}
            {ticks.map((t) => (
              <g key={t}>
                <line
                  x1={margeGauche}
                  x2={largeur - MARGE_DROITE}
                  y1={y(t)}
                  y2={y(t)}
                  stroke={t === 0 && bas < 0 ? theme.palette.text.disabled : theme.palette.divider}
                  strokeWidth={1}
                  shapeRendering="crispEdges"
                />
                <text
                  x={margeGauche - 8}
                  y={y(t)}
                  dy="0.35em"
                  textAnchor="end"
                  fontSize={11}
                  fill={theme.palette.text.secondary}
                  style={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  {formaterAxe(t)}
                </text>
              </g>
            ))}

            {/* Colonnes, empilées : un écart de 2 px en couleur de surface
                sépare les segments, jamais un contour. */}
            {libelles.map((_, i) => {
              let basPos = 0
              let basNeg = 0
              let premierPos = true
              let premierNeg = true
              const segments: { k: number; y0: number; y1: number; bout: boolean }[] = []
              const derniers = { pos: -1, neg: -1 }
              colonnes.forEach((s, k) => {
                const v = s.valeurs[i]
                if (v) {
                  if (v > 0) derniers.pos = k
                  else derniers.neg = k
                }
              })
              colonnes.forEach((s, k) => {
                const v = s.valeurs[i]
                if (!v) return
                const positif = v > 0
                const debut = positif ? basPos : basNeg
                const fin = debut + v
                if (positif) basPos = fin
                else basNeg = fin
                let y0 = y(debut)
                const y1 = y(fin)
                const premier = positif ? premierPos : premierNeg
                if (!premier && Math.abs(y1 - y0) > ECART + 1) y0 += positif ? -ECART : ECART
                if (positif) premierPos = false
                else premierNeg = false
                segments.push({ k, y0, y1, bout: k === (positif ? derniers.pos : derniers.neg) })
              })
              return (
                <g key={i} opacity={actif !== null && actif !== i ? 0.45 : 1}>
                  {segments.map((seg) => (
                    <path
                      key={seg.k}
                      d={chemin(cx(i) - lb / 2, lb, seg.y0, seg.y1, seg.bout)}
                      fill={couleurDe(seg.k)}
                    />
                  ))}
                </g>
              )
            })}

            {ligne && traceLigne ? (
              <g opacity={actif !== null ? 0.9 : 1}>
                <path
                  d={traceLigne}
                  fill="none"
                  stroke={couleurDe(series.length - 1)}
                  strokeWidth={2}
                  strokeLinejoin="round"
                  strokeLinecap="round"
                />
                {/* Point d'extrémité, cerclé de surface pour rester lisible
                    là où il croise une colonne. */}
                {pointFinal ? (
                  <circle
                    cx={pointFinal.x}
                    cy={pointFinal.y}
                    r={4}
                    fill={couleurDe(series.length - 1)}
                    stroke={surface}
                    strokeWidth={2}
                  />
                ) : null}
                {actif !== null && ligne.valeurs[actif] !== null ? (
                  <circle
                    cx={cx(actif)}
                    cy={y(ligne.valeurs[actif] ?? 0)}
                    r={4}
                    fill={couleurDe(series.length - 1)}
                    stroke={surface}
                    strokeWidth={2}
                  />
                ) : null}
              </g>
            ) : null}

            {!ligne && totalFinal !== 0 && n > 0 ? (
              <text
                x={cx(n - 1) + lb / 2}
                y={y(Math.max(0, totalFinal)) - 6}
                textAnchor="end"
                fontSize={11}
                fontWeight={700}
                fill={theme.palette.text.primary}
              >
                {formaterAxe(totalFinal)}
              </text>
            ) : null}

            {/* Étiquettes des tranches. */}
            {libelles.map((l, i) =>
              (n - 1 - i) % pasEtiquette === 0 ? (
                <text
                  key={i}
                  x={cx(i)}
                  y={HAUTEUR - 8}
                  textAnchor="middle"
                  fontSize={11}
                  fontWeight={i === n - 1 ? 700 : 400}
                  fill={i === n - 1 ? theme.palette.text.primary : theme.palette.text.secondary}
                >
                  {l}
                </text>
              ) : null,
            )}

            {/* Cibles de survol : la TRANCHE entière, pas les seuls pixels
                peints — une colonne de 7 px ne se vise pas. */}
            {libelles.map((_, i) => (
              <rect
                key={i}
                x={margeGauche + slot * i}
                y={MARGE_HAUT}
                width={slot}
                height={plotH}
                fill="transparent"
                onPointerEnter={() => setActif(i)}
                onPointerDown={() => setActif(i)}
              />
            ))}
          </svg>
        ) : null}

        {actif !== null && !vide ? (
          <Box
            role="status"
            aria-live="polite"
            sx={{
              position: 'absolute',
              // ⚠️ DANS le graphe, à côté de la colonne — jamais au-dessus :
              // la boîte de dialogue rogne ce qui dépasse de son contenu, et
              // l'infobulle perdait sa date. Du côté où il reste de la place.
              top: MARGE_HAUT,
              ...(cx(actif) > largeur / 2
                ? { right: largeur - cx(actif) + lb / 2 + 8 }
                : { left: cx(actif) + lb / 2 + 8 }),
              whiteSpace: 'nowrap',
              bgcolor: 'background.paper',
              border: 1,
              borderColor: 'divider',
              borderRadius: 2,
              boxShadow: 3,
              px: 2,
              py: 1.5,
              minWidth: 160,
              pointerEvents: 'none',
              zIndex: 1,
            }}
          >
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
              {libellesLongs[actif]}
            </Typography>
            {series.map((s, k) => {
              const v = s.valeurs[actif]
              return (
                <Box
                  key={s.nom}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1.5, justifyContent: 'space-between' }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 12, height: 2, borderRadius: 1, bgcolor: couleurDe(k) }} />
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {s.nom}
                    </Typography>
                  </Box>
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>
                    {v === null || v === undefined ? '—' : formater(v)}
                  </Typography>
                </Box>
              )
            })}
          </Box>
        ) : null}
      </Box>
    </Box>
  )
}
